import { useId, useState, type FormEvent } from 'react';
import { Button, Card, CardBody, Field, Input } from '@herbo/ui-tokens';
import { CircleAlert, TriangleAlert } from 'lucide-react';
import { useAuth } from '../../lib/auth-context';
import { tenant } from '../../lib/tenant';

/**
 * Écran de connexion — seule chose affichée tant que la session n'est pas
 * établie (garde de navigation, cf. App.tsx). Sobre, français simple, jamais
 * de code d'erreur technique : cf. §3/§5 du contrat gelé
 * `CONTRACT-AUTHENTIFICATION.md`.
 */
export function LoginPage(): JSX.Element {
  const { login, bootError, retry } = useAuth();
  const emailId = useId();
  const passwordId = useId();

  const [email, setEmail] = useState('');
  const [motDePasse, setMotDePasse] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  async function handleSubmit(e: FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();
    if (submitting) return; // pas de double soumission
    setSubmitting(true);
    setFormError(null);
    try {
      await login(email, motDePasse);
      // Succès : le contexte passe en 'authenticated', App.tsx démonte cet
      // écran — rien d'autre à faire ici.
    } catch (err) {
      setFormError(err instanceof Error ? err.message : 'Une erreur est survenue. Réessayez dans un instant.');
      setSubmitting(false);
    }
  }

  const initiales = tenant.shortName.slice(0, 1).toUpperCase();

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4">
      <div className="w-full max-w-[380px] min-w-0">
        <div className="flex flex-col items-center gap-2 mb-6">
          {tenant.logoUrl ? (
            <img src={tenant.logoUrl} alt="" className="w-10 h-10 rounded-lg" />
          ) : (
            <div
              className="w-10 h-10 rounded-lg grid place-items-center text-white text-base font-bold"
              style={{ background: 'var(--accent)' }}
              aria-hidden="true"
            >
              {initiales}
            </div>
          )}
          <div className="text-slate-900 font-semibold text-base">{tenant.name}</div>
          <div className="text-slate-400 text-xs">Connexion à votre espace</div>
        </div>

        <Card>
          <CardBody>
            {bootError && (
              <div
                role="alert"
                className="mb-4 flex items-start gap-2 text-[13px] text-[var(--warn-fg)] bg-[var(--warn-soft)] border border-[var(--warn)] rounded-[8px] p-[10px]"
              >
                <TriangleAlert size={16} className="shrink-0 mt-[1px]" aria-hidden="true" />
                <div className="flex-1 min-w-0">
                  <div>{bootError}</div>
                  <button
                    type="button"
                    onClick={retry}
                    className="mt-1 font-medium underline underline-offset-2 hover:no-underline"
                  >
                    Réessayer
                  </button>
                </div>
              </div>
            )}

            <form onSubmit={handleSubmit}>
              <div className="flex flex-col gap-4">
                <Field label="Adresse email" htmlFor={emailId}>
                  <Input
                    id={emailId}
                    type="email"
                    autoComplete="username"
                    autoFocus
                    required
                    disabled={submitting}
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                  />
                </Field>
                <Field label="Mot de passe" htmlFor={passwordId}>
                  <Input
                    id={passwordId}
                    type="password"
                    autoComplete="current-password"
                    required
                    disabled={submitting}
                    value={motDePasse}
                    onChange={(e) => setMotDePasse(e.target.value)}
                  />
                </Field>

                {formError && (
                  <div
                    role="alert"
                    className="flex items-start gap-2 text-[13px] text-[var(--danger-fg)] bg-[var(--danger-soft)] border border-[var(--danger)] rounded-[8px] p-[10px]"
                  >
                    <CircleAlert size={16} className="shrink-0 mt-[1px]" aria-hidden="true" />
                    <span className="flex-1 min-w-0">{formError}</span>
                  </div>
                )}

                <Button type="submit" variant="primary" size="lg" loading={submitting} className="w-full justify-center">
                  {submitting ? 'Connexion…' : 'Se connecter'}
                </Button>
              </div>
            </form>
          </CardBody>
        </Card>
      </div>
    </div>
  );
}
